import React, {useContext, useState} from 'react';
import {Button, Modal, Switch, Tag} from "antd";
import {AuthContext} from "../../../shared/contexts/authContext";
import {internalApi} from "../../../common/axios";
import {ENV, REFRESH_TYPE_FUNCTION} from "../../../shared/utils/enum";
import dayjs from "dayjs";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import GameManagerEditModal from "../GameManagerEditModal";
import TablePage from "../../../pages/tablePage/TablePage";
import {MergeEventMasterEntity} from "./MergeEventMasterEntity";
import {
    deleteMergeEventMaster,
    saveMergeEventMaster,
    useMergeEventMasterData
} from "./useMergeEventMaster";
import toast from "react-hot-toast";
import {useReloadTime} from "../../serverInfo/useServerInfo";
import {useMergeEventMasterTableData} from "../../logs/useTableData";

const MergeEventManager = () => {
    const {currentUser} = useContext(AuthContext);
    const [rowData, setRowData] = useState({});
    const {data, isLoading, error, refetch} = useMergeEventMasterData();
    const {data: mergeEventMasterTable, isLoading: isLoadingMergeEventMasterTable, error: mergeEventMasterTableError} = useMergeEventMasterTableData();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();
    // On/Off 스위치 토글 핸들러
    const handleSwitchChange = async (checked, record) => {
        try {
            if (record.end < dayjs().unix() && record.onOff !== 1) {
                toast.error('종료된 머지 이벤트는 On/Off 상태를 변경할 수 없습니다.');
                return;
            }

            record.onOff = checked ? 1 : 0;
            await saveMergeEventMaster(record);
            await refetch();
        } catch (e) {
            toast.error('On/Off 변경 실패');
        }
    };

    const handleCopyLiveData = async () => {
        const result = await internalApi.put('merge_event_master');
        if (result) {
            toast.success('라이브 데이터 복사가 완료되었습니다.');
            await refetch();
        }
    };

    const showCopyConfirm = () => {
        Modal.confirm({
            title: 'Live 데이터 복사',
            content: '현재 환경의 모든 머지 이벤트 데이터가 Live 데이터로 덮어쓰여집니다. 계속하시겠습니까?',
            okText: '확인',
            cancelText: '취소',
            onOk: handleCopyLiveData,
        });
    };

    const columns = [
        {
            title: '시퀀스',
            dataIndex: 'seq',
            key: 'seq',
            width: 150,
        },
        {
            title: '인덱스',
            dataIndex: 'index',
            key: 'index',
            width: 150,
        },
        {
            title: '이름',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '머지 이벤트 타입',
            dataIndex: 'mergeEventType',
            key: 'mergeEventType',
            width: 150,
        },
        {
            title: '오픈 레벨',
            dataIndex: 'openLevel',
            key: 'openLevel',
            width: 100,
        },
        {
            title: 'On/Off',
            dataIndex: 'onOff',
            render: (_, record) => (
                <Switch
                    checked={record.onOff === 1}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={checked => handleSwitchChange(checked, record)}
                    onClick={(_, e) => e.stopPropagation()}
                />
            ),
            filters: [
                {
                    text: 'On',
                    value: 1,
                },
                {
                    text: 'Off',
                    value: 0,
                },
            ],
            width: 100,
        },
        {
            title: '시작 시간',
            dataIndex: 'begin',
            key: 'begin',
            width: 200,
            render: (_, {begin}) => {
                return dayjs.unix(begin).format('YYYY-MM-DD HH:mm:ss');
            }
        },
        {
            title: '종료 시간',
            dataIndex: 'end',
            key: 'end',
            width: 200,
            render: (_, {end}) => {
                return dayjs.unix(end).format('YYYY-MM-DD HH:mm:ss');
            }
        },
        {
            title: '최소 적용 버전',
            dataIndex: 'minVersion',
            key: 'minVersion',
            render: (_, {index}) => (
                <Tag>{mergeEventMasterTable.find(e => e.index === index)?.openVersion || '테이블에 값을 넣어주세요.'}</Tag>
            )
        },
    ];

    if (isLoading || isReloadTimeData || isLoadingMergeEventMasterTable) return <IsLoading/>;
    const fetchError = error || reloadTimeDataError || mergeEventMasterTableError;
    if (fetchError) return <ErrorPage error={fetchError}/>;

    const props = {
        columns,
        rowData,
        setRowData,
        isLoading: isLoading,
        localData: data || [],
        entityModel: MergeEventMasterEntity,
        modal: (modalProps) => <GameManagerEditModal {...modalProps} handleClose={() => { modalProps.handleClose(); refetch(); }} />,
        modalOptions: {
            save: saveMergeEventMaster,
            delete: deleteMergeEventMaster,
            entity: MergeEventMasterEntity,
            isMergeEvent: true,
        },
        refreshTypeFunc: REFRESH_TYPE_FUNCTION.GAME_DATA,
        reloadTime: reloadTimeData?.game_data_sync_id,
        reloadTimeRefetch: reloadTimeDataRefetch
    }

    return (
        <div>
            {currentUser.target !== ENV.PRODUCTION && (
                <Button
                    style={{marginBottom: 16, marginRight: 16}}
                    onClick={showCopyConfirm}
                >
                    Live 데이터 복사하기
                </Button>
            )}
            <TablePage {...props} />
        </div>
    );
};

export default MergeEventManager;